// Libs
import classNames from 'classnames/bind';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
// Components, Layouts, Pages
import { BaseInput, Loading } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { handleLogin } from '~/thunks/auth/authThunk';
import { generateGuid } from '~/utils/helper/common';
// import { staffRouteAbsolute } from '~/utils/routes/staff';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { authRouteAbsolute } from '~/utils/routes/auth';
import { IFormLogin } from '~/utils/interface/auth';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
import { defaultValuesForm, schema } from '~/pages/common/signIn/helper';
import { authActions } from '~/thunks/auth/authSlice';
// Styles, images, icons
import styles from './SignIn.module.scss';

const cx = classNames.bind(styles);

const SignIn = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/auth']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormLogin>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    const token = localStorage.getItem(StorageEnum.ACCESS_TOKEN);
    const role = localStorage.getItem(StorageEnum.ROLE);

    if (!token || !role) return;

    switch (role) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.dashboard, { replace: true });
        break;
      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.dashboard, { replace: true });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSubmitFormSignIn = (data: IFormLogin) => {
    setIsLoading(true);
    const dataLogin: IFormLogin = {
      ...data,
      allowedRoles: [RoleEnum.ADMIN, RoleEnum.STAFF],
    };

    dispatch(handleLogin(dataLogin))
      .unwrap()
      .then((res) => {
        const { account, token } = res?.data;
        localStorage.setItem(StorageEnum.ACCESS_TOKEN, token.access);
        localStorage.setItem(StorageEnum.REFRESH_TOKEN, token.refresh);
        localStorage.setItem(StorageEnum.ROLE, account.role);
        localStorage.setItem(StorageEnum.ACCOUNT_ID, account.id);
        localStorage.setItem(StorageEnum.DEVICE_ID, generateGuid());

        dispatch(authActions.setAccessToken(token.access));

        switch (account.role) {
          case RoleEnum.ADMIN:
            navigate(`${adminRouteAbsolute.dashboard}`);
            break;
          case RoleEnum.STAFF:
            navigate(`${staffRouteAbsolute.dashboard}`);
            break;
        }
        reset();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleNavigateToForgotPassword = () => {
    navigate(`${authRouteAbsolute.forgotPassword}`);
  };
  //#endregion Handle Function

  return (
    <Box
      id='signInCommonPage'
      className={cx('container')}
      component={'form'}
      onSubmit={handleSubmit(handleSubmitFormSignIn)}
    >
      <Typography className={cx('loginTitle')}>{t('title_sign_in')}</Typography>

      <Stack direction={'column'} spacing={'30px'}>
        <Stack direction={'column'} spacing={'20px'}>
          <Controller
            name='email'
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                height={50}
                required
                label={t('form_login_label_email')}
                value={value}
                onChange={onChange}
                errorMessage={errors?.email?.message}
              />
            )}
          />

          <Controller
            name='password'
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                type='password'
                height={50}
                required
                label={t('form_login_label_password')}
                value={value}
                onChange={onChange}
                errorMessage={errors?.password?.message}
              />
            )}
          />
        </Stack>

        <Button variant='contained' className={cx('loginButtonLogin')} type='submit' size='large'>
          {t('form_login_btn_sign_in')}
        </Button>
      </Stack>

      <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'}>
        <Typography
          align='right'
          component={'span'}
          className={cx('loginButtonForgot')}
          onClick={handleNavigateToForgotPassword}
        >
          {t('form_login_btn_forgot')}
        </Typography>
      </Stack>

      <Loading loading={isLoading} />
    </Box>
  );
};

export default SignIn;
